// Shared resolver contract for the admin Setup Guide.
// Every resolver returns ResolverResult via a named function. The type used to
// live in hoursResolver.ts, which made it feel like a hours-specific export;
// it now lives here so the dependency graph between resolvers is flat.
//
// §1.7.X consolidation: resolvers receive `ResolverInput` with a
// parent-fetched `progress` snapshot. Resolvers MUST NOT call
// `supabase.rpc('get_setup_progress')` themselves — the parent hook fetches it
// once and passes it down. See references/SETUP_ITEMS_CONTRACT_v1.md Rule 1.

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Business } from '../../../types/business';

export interface ResolverResult {
  complete: boolean;
  reason: string;
}

// Mirror of the jsonb payload returned by the `get_setup_progress(p_business_id uuid)`
// RPC as of migration 133. Keep the field list in lockstep with the migration.
export interface SetupProgress {
  has_team: boolean;
  has_shift: boolean;
  has_job: boolean;
  has_vehicle: boolean;
  has_booking: boolean;
  has_invoice: boolean;
  has_project: boolean;
  has_location: boolean;
  has_service: boolean;
  has_business_hours: boolean;
  has_tax: boolean;
  has_payment_received: boolean;
  has_imported_clients: boolean;
  has_commission_tracking: boolean;
  has_booking_page: boolean;
  has_stripe_connected: boolean;
  invoice_logo_url: string | null;
  // §1.1 migration 140 additions
  has_business_profile: boolean;
  has_invoice_branding: boolean;
  has_intro_offers: boolean;
  hours_customized: boolean;
  services_customized: boolean;
  // Today Hub Spec v1 §4.3a (migration 20261103232558) — seeded-but-unconfirmed
  // flags; paired with crewbit-mobile/data/setupGuideCatalog.ts SetupProgress.
  hours_unconfirmed: boolean;
  services_unconfirmed: boolean;
  booking_page_unconfirmed: boolean;
}

// Zero-filled default used by the parent hook when the RPC errors. Every
// boolean false, logo_url null — every resolver will mark its item incomplete.
export const EMPTY_SETUP_PROGRESS: SetupProgress = {
  has_team: false,
  has_shift: false,
  has_job: false,
  has_vehicle: false,
  has_booking: false,
  has_invoice: false,
  has_project: false,
  has_location: false,
  has_service: false,
  has_business_hours: false,
  has_tax: false,
  has_payment_received: false,
  has_imported_clients: false,
  has_commission_tracking: false,
  has_booking_page: false,
  has_stripe_connected: false,
  invoice_logo_url: null,
  has_business_profile: false,
  has_invoice_branding: false,
  has_intro_offers: false,
  hours_customized: false,
  services_customized: false,
  hours_unconfirmed: false,
  services_unconfirmed: false,
  booking_page_unconfirmed: false,
};

export interface ResolverInput {
  businessId: string;
  progress: SetupProgress;
  // Legacy direct-DB resolvers still need the client until they migrate to
  // the RPC. New resolvers should read from `progress` instead.
  supabase: SupabaseClient;
  // Some resolvers read fields off the business directly (tax_mode, etc).
  business: Business;
}

export type SetupGuideResolver = (input: ResolverInput) => Promise<ResolverResult>;
